import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/ui";
import { Tables } from "@/integrations/supabase/types";

export interface School {
  id: string;
  subscription_id: string;
  school_name: string;
  email: string;
  phone?: string;
  status: "active" | "inactive" | "expired";
  subscription_end?: string;
  students_count: number;
  created_at: string;
  updated_at: string;
}

export interface NewSchoolData {
  school_name: string;
  phone?: string;
  adminName?: string;
  adminEmail?: string;
  subscription_months?: number;
}

interface SchoolCreationResponse {
  success: boolean;
  error?: string;
  details?: string;
}
export const useSchools = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const fetchSchools = async () => {
    try {
      console.log('[fetchSchools] Starting to fetch schools...');

      // أولاً جلب بيانات المدارس
      const { data: schoolsData, error: schoolsError } = await supabase
        .from("schools")
        .select("*")
        .order("created_at", { ascending: false });

      if (schoolsError) {
        console.error("[fetchSchools] ❌ Error fetching schools:", schoolsError);
        throw schoolsError;
      }

      console.log("[fetchSchools] ✅ Fetched", schoolsData?.length || 0, 'schools');

      // ثم جلب عدد الطلاب لكل مدرسة
      const schoolsWithStudentCount = await Promise.all(
        (schoolsData || []).map(async (school) => {
          try {
            console.log('[fetchSchools] Fetching student count for school:', school.id);
            const { count, error: countError } = await supabase
              .from("students")
              .select("*", { count: "exact", head: true })
              .eq("school_id", school.id);

            if (countError) {
              console.error(
                "[fetchSchools] ❌ Error counting students for school:",
                school.id,
                countError
              );
            }

            return {
              ...school,
              status: school.status as "active" | "inactive" | "expired",
              phone: school.phone || undefined,
              subscription_end: school.subscription_end || undefined,
              students_count: count || 0,
            };
          } catch (err) {
            console.error('[fetchSchools] Error processing school', school.id, err);
            return {
              ...school,
              status: school.status as "active" | "inactive" | "expired",
              phone: school.phone || undefined,
              subscription_end: school.subscription_end || undefined,
              students_count: 0,
            };
          }
        })
      );

      console.log('[fetchSchools] ✅ Final schools list with counts:', schoolsWithStudentCount.length);
      setSchools(schoolsWithStudentCount);
    } catch (error) {
      console.error("[fetchSchools] ❌ Error fetching schools:", error);
      toast({
        title: "خطأ في تحميل البيانات",
        description: "لم نتمكن من تحميل بيانات المدارس",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  const getSchoolAdmin = async (
    schoolId: string
  ): Promise<Tables<"profiles_with_user"> | null> => {
    try {
      // console.log('Fetching schools...')
      const { error, data } = await supabase
        .from("profiles_with_user")
        .select("*")
        .eq("role", "school")
        .eq("school_id", schoolId)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error fetching schools:", error);
      toast({
        title: "خطأ في تحميل البيانات",
        description: "لم نتمكن من تحميل بيانات المدير",
        variant: "destructive",
      });

      return null;
    }
  };

  const createSchool = async (
    schoolData: NewSchoolData
  ): Promise<{
    school?: School;
    credentials?: { email: string; password: string; subscription_id: string };
  }> => {
    try {
      console.log("[createSchool] Starting school creation process...");
      console.log("[createSchool] School data:", schoolData);

      // Validate input
      if (!schoolData.school_name?.trim()) {
        throw new Error("اسم المدرسة مطلوب");
      }

      console.log("[createSchool] Calling Supabase edge function 'create-school'");
      const { data, error } = await supabase.functions.invoke("create-school", {
        body: schoolData,
      });

      console.log("[createSchool] Edge function response:", { data, error });

      if (error) {
        console.error("[createSchool] ❌ Edge function error:", error);
        console.error("[createSchool] Error message:", error.message);
        console.error("[createSchool] Error status:", error.status);
        
        let errorMessage = "فشل في إنشاء المدرسة - خطأ في الخدمة";
        let errorDetails = null;

        try {
          if (error.context?.json) {
            errorDetails = await error.context.json();
            console.error("[createSchool] Error details from context:", errorDetails);
          }
        } catch (parseErr) {
          console.error("[createSchool] Failed to parse error context:", parseErr);
        }

        // create-school returns { success, error, details } — not always { message }
        const apiErrorText =
          (typeof errorDetails?.error === "string" && errorDetails.error) ||
          (typeof errorDetails?.message === "string" && errorDetails.message) ||
          (typeof errorDetails?.details === "string" && errorDetails.details) ||
          null;

        if (apiErrorText) {
          errorMessage = apiErrorText;
        }

        console.error("[createSchool] Final error message:", errorMessage);
        throw new Error(errorMessage);
      }

      if (!data) {
        console.error("[createSchool] ❌ No data received from edge function");
        throw new Error("لم يتم تلقي استجابة من الخدمة");
      }

      console.log("[createSchool] Edge function response data:", data);

      if (!data.success) {
        console.error("[createSchool] ❌ School creation failed:", data.error);
        throw new Error(data.error || "فشل في إنشاء المدرسة");
      }

      console.log("[createSchool] ✅ School created successfully");

      // Refresh the schools list
      await fetchSchools();

      toast({
        title: "تم إنشاء المدرسة بنجاح",
        // description: `رقم الاشتراك: ${data.credentials.subscription_id}`,
      });

      return {
        school: data.school,
        credentials: {
          email: data.credentials.email,
          password: data.credentials.password,
          subscription_id: data.credentials.subscription_id,
        },
      };
    } catch (error) {
      console.error("[createSchool] ❌ Error in createSchool:", error);
      console.error("[createSchool] Error type:", typeof error);
      console.error("[createSchool] Error stack:", (error as any)?.stack);

      let errorMessage = "حدث خطأ أثناء إنشاء المدرسة";
      if (error instanceof Error) {
        errorMessage = error.message;
        console.error("[createSchool] Extracted error message:", errorMessage);
      }

      toast({
        title: "فشل في إنشاء المدرسة",
        description: errorMessage,
        variant: "destructive",
      });

      return {};
    }
  };

  const updateSchoolStatus = async (
    schoolId: string,
    status: "active" | "inactive" | "expired"
  ) => {
    try {
      const { error } = await supabase
        .from("schools")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", schoolId);

      if (error) throw error;

      await fetchSchools();
      toast({
        title: "تم تحديث حالة المدرسة",
        description: `تم ${status === "active" ? "تفعيل" : "إيقاف"} المدرسة`,
      });
    } catch (error) {
      console.error("Error updating school status:", error);
      toast({
        title: "خطأ في التحديث",
        description: "لم نتمكن من تحديث حالة المدرسة",
        variant: "destructive",
      });
    }
  };

  const extendSubscription = async (schoolId: string, months: number) => {
    try {
      const school = schools.find((s) => s.id === schoolId);
      if (!school) return;

      const currentEnd = school.subscription_end
        ? new Date(school.subscription_end)
        : new Date();
      const newEnd = new Date(currentEnd);
      newEnd.setMonth(newEnd.getMonth() + months);

      const { error } = await supabase
        .from("schools")
        .update({
          subscription_end: newEnd.toISOString().split("T")[0],
          status: "active",
          updated_at: new Date().toISOString(),
        })
        .eq("id", schoolId);

      if (error) throw error;

      await fetchSchools();
      toast({
        title: "تم تمديد الاشتراك",
        description: `تم تمديد الاشتراك لمدة ${months} شهر`,
      });
    } catch (error) {
      console.error("Error extending subscription:", error);
      toast({
        title: "خطأ في التمديد",
        description: "لم نتمكن من تمديد الاشتراك",
        variant: "destructive",
      });
    }
  };

  const deleteSchool = async (schoolId: string) => {
    setDeleting(true);
    try {
      // حذف جميع البيانات المرتبطة بالمدرسة
      // حذف سجلات الحضور
      await supabase
        .from("late_attendance_records")
        .delete()
        .eq("school_id", schoolId);
      await supabase
        .from("attendance_records")
        .delete()
        .eq("school_id", schoolId);

      // حذف الطلاب
      await supabase.from("students").delete().eq("school_id", schoolId);

      // حذف إعدادات المدرسة
      await supabase.from("school_settings").delete().eq("school_id", schoolId);
      await supabase.from("profiles").delete().eq("school_id", schoolId);

      // حذف ملف المدرسة من جدول المستخدمين

      // حذف المدرسة نفسها أخيراً
      const { error } = await supabase
        .from("schools")
        .delete()
        .eq("id", schoolId);

      if (error) throw error;

      await fetchSchools();
      toast({
        title: "تم حذف المدرسة",
        description: "تم حذف المدرسة وجميع البيانات المرتبطة بها",
      });
    } catch (error) {
      console.error("Error deleting school:", error);
      toast({
        title: "خطأ في الحذف",
        description: "لم نتمكن من حذف المدرسة",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const updateSchoolEmail = async (schoolId: string, newEmail: string) => {
    try {
      const { error } = await supabase
        .from("schools")
        .update({
          email: newEmail,
          updated_at: new Date().toISOString(),
        })
        .eq("id", schoolId);

      if (error) throw error;

      await fetchSchools();
      toast({
        title: "تم تحديث البريد الإلكتروني",
        description: "تم تحديث البريد الإلكتروني للمدرسة بنجاح",
      });
    } catch (error) {
      console.error("Error updating school email:", error);
      toast({
        title: "خطأ في التحديث",
        description: "لم نتمكن من تحديث البريد الإلكتروني",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  return {
    schools,
    loading,
    deleting,
    createSchool,
    updateSchoolStatus,
    updateSchoolEmail,
    extendSubscription,
    deleteSchool,
    refetch: fetchSchools,
    getSchoolAdmin,
  };
};
